import {
  classes,
  getAll,
  getCurrentOwnerSub,
  getOutboxOp,
  horses,
  isBlockedByUnsyncedRide,
  isHeldOp,
  isOwnerless,
  peekOutbox,
  people,
  put,
  removeOutboxOp,
  rides,
  scoreSheets,
  setCurrentOwnerSub,
  setMeta,
  shows,
  tests,
  updateOutboxOp,
} from "./db";

// Sync engine for the local-first model (card #420).
//
// The IndexedDB cache is the source of truth; this engine is the background
// worker that (a) drains the outbox — pushing queued mutations to score-api in
// FIFO order — and (b) hydrates the cache by pulling fresh entity lists. It
// runs whenever the app is "effectively online":
//
//   effectiveOnline = browserOnline && !forcedOffline
//
// `browserOnline` tracks navigator.onLine / window online-offline events;
// `forcedOffline` is the banner toggle (demo/manual offline). Both are pushed
// in by ScoreContext. A short flush loop keeps queued scores flowing to the
// cloud in near-real-time while online.
//
// The engine is framework-agnostic and takes its API + timers by injection so
// it can be unit-tested with a fake client and fake-indexeddb. `api` is a thin
// adapter (built by ScoreContext from the score-common clients, unwrapping the
// JSend envelope) with this shape:
//
//   createRide(payload) -> ride            listShows() -> shows[]
//   createScoreSheet(body) -> sheet        listClasses(showId) -> classes[]
//   getScoreSheet(id) -> sheet   (#2409)   listRides(classId) -> rides[]
//   updateScoreSheet(id, body) -> sheet    getTest(testId) -> test
//   completeScoreSheet(id) -> sheet
//   confirmScoreSheetMarks(id) -> sheet    (#698 — one-way marks freeze)
//   panelSignScoreSheet(id, judgeId) -> sheet   (#1751 — one panel judge signs)
//   listPeople() -> people[]               listHorses() -> horses[]
//   raiseUtilityEvent(rideId, type) -> event    (card #139)
//   clearUtilityEvent(rideId, eventId) -> {}    (card #139)

// #1980/perf — refreshPending runs on every flush tick (every flushIntervalMs
// while online, forever, on a scribe kiosk). Every field on the snapshot used
// to be a primitive, so React's setState bailed out when nothing changed; a
// freshly-allocated Set every tick broke that bailout and re-rendered the
// whole ScoreProvider subtree on a timer even when the outbox was quiet.
// Comparing contents here lets refreshPending keep handing out the SAME Set
// instance when nothing changed, restoring the bailout.
function setsEqual(a, b) {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

export function createSyncEngine({
  api = null,
  // #1289 — called when an op 401s (#1562: NOT on a 403, see classifyError
  // below). Should attempt a silent token renew and resolve truthy if it
  // succeeded. The app-wide singleton is
  // constructed at import time with nothing here and gets the real handler
  // later via setAuthFailureHandler (ScoreContext owns the OIDC client); tests
  // inject it directly. Null means "no renew available", which degrades to
  // surfacing the session-expired state immediately rather than retrying
  // forever with a credential we already know is dead.
  onAuthFailure = null,
  flushIntervalMs = 5000,
  // Arrow-wrapped so setInterval/clearInterval are invoked with the global
  // `this`. Passing bare references and calling them as `timers.set(...)`
  // detaches `this` and Chrome throws "Illegal invocation" (WebIDL globals).
  timers = {
    set: (fn, ms) => setInterval(fn, ms),
    clear: (id) => clearInterval(id),
  },
} = {}) {
  let apiRef = api;
  let authHandler = onAuthFailure;
  let browserOnline = true;
  let forcedOffline = false;
  let intervalId = null;
  let draining = false;
  let pendingCount = 0;
  let errorCount = 0;
  // #1921 — of errorCount, how many have reached the terminal (give-up) state:
  // a permanent (non-401 4xx) failure that will never clear on its own. Kept
  // apart from errorCount so the UI can tell "still retrying" from "stuck,
  // needs a human" without re-deriving it from raw ops.
  let terminalCount = 0;
  // #1923 — of the outbox, how many ops are HELD: owned by a sub other than
  // whoever is currently signed in, or by no one known (a pre-#1923 legacy
  // row). Held ops are never sent (see the `ready` filter in flush()) and
  // never dropped — only an explicit discard removes one. Kept apart from
  // terminalCount: a held op that's ALSO already terminal (a pre-#1923
  // op that got a real 403 under its actual owner, back when that owner
  // really was signed in) keeps the existing terminal/403 treatment instead
  // of the newer "not sent" wording, which would be false for it — it WAS
  // sent, that's how it got rejected.
  let heldCount = 0;
  // #2031 AC3 — of the outbox, how many ops are BLOCKED: a dependent
  // (tempId) op whose own createRide op is itself genuinely stuck (surfaced
  // as an error/terminal, or held) rather than merely mid-retry. Kept apart
  // from heldCount: a blocked op is not held on OWNERSHIP grounds (its own
  // owner may match the current sub fine) — it is withheld by the #882
  // ordering gate instead, a different cause with a different remedy
  // (nothing the scribe does to THIS op fixes it; the createRide op does).
  let blockedCount = 0;
  // #1980 — entityKey (the manual ride's tempId) for every createRide op
  // that has surfaced as an error, so ScoreContext can exclude that ride
  // from the separate "N unsynced rides" count: once a stuck createRide op
  // is reported by the sync-issue chip (errorCount), the ride must not ALSO
  // light up the unsynced-rides chip — see manualRides.js for the other
  // half of this fix.
  let erroredRideKeys = new Set();
  // #882 — a dedicated one-shot timer for stranded (status:"error") ops, so
  // they get retried on their own backoff schedule rather than only on the
  // coarse flushIntervalMs poll or an explicit reconnect. Separate from
  // `intervalId` (the steady poll), which keeps running independently.
  let retryTimerId = null;
  // #1289 — true once an auth failure has happened AND the silent renew that
  // followed it did not recover the session. Deliberately NOT set on the raw
  // 401: a merely-expired token that renews cleanly must stay invisible to the
  // scribe. Only a renew that failed means a human has to act.
  let authExpired = false;
  let renewInFlight = false;
  // #1289 — silent renews that have bought us nothing. Reset to 0 by any op
  // that actually applies. Without this cap, a token that renews CLEANLY but
  // that score-api still rejects (exactly the stage 2026-07-28 case: the JWT
  // verified fine client-side, the API threw ERR_JOSE_NOT_SUPPORTED on its
  // "alg") would renew-and-fail every poll, forever, showing the scribe
  // nothing — the same retry-forever defect this card exists to kill, just
  // quieter. After MAX_SILENT_RENEWS fruitless rounds we stop guessing and put
  // it in front of a human.
  let renewsSinceProgress = 0;
  const listeners = new Set();
  const rideSyncedListeners = new Set();

  const isOnline = () => browserOnline && !forcedOffline;

  const snapshot = () => ({
    online: isOnline(),
    pendingSync: pendingCount,
    errorSync: errorCount,
    terminalSync: terminalCount,
    heldSync: heldCount,
    blockedSync: blockedCount,
    authExpired,
    erroredRideKeys,
  });

  function emit() {
    const s = snapshot();
    for (const fn of listeners) fn(s);
  }

  function subscribe(fn) {
    listeners.add(fn);
    fn(snapshot());
    return () => listeners.delete(fn);
  }

  // Notified when a temp ride id is replaced by its server id (so the manual-
  // ride store can stamp the serverId onto its local mirror).
  function onRideSynced(fn) {
    rideSyncedListeners.add(fn);
    return () => rideSyncedListeners.delete(fn);
  }

  // #1923 — "held" is deliberately excluded once an op is already terminal;
  // see the heldCount comment above for why the two states carry different
  // (and not simultaneously true) wording.
  function isHeldForCurrentOwner(op) {
    return op.status !== "terminal" && isHeldOp(op, getCurrentOwnerSub());
  }

  // #2031 AC3 / #882 — whether a dependent (tempId) op should be surfaced as
  // BLOCKED rather than looking like ordinary healthy pending work. Not
  // merely isBlockedByUnsyncedRide (db.js): that fires the instant a
  // createRide op is enqueued, including its first few seconds of normal
  // backoff retry, and surfacing every dependent op through that window
  // would repeat the exact "cry wolf on a momentary blip" problem #914's
  // SURFACE_AFTER_ATTEMPTS debounce exists to prevent (see isSurfacedError
  // above). This only fires once the createRide op ITSELF is something
  // isSurfacedError or isHeldForCurrentOwner already calls genuinely stuck
  // (a permanent/terminal failure, a transient failure that survived past
  // its own debounce, or an ownership hold) — the "blocked forever" case the
  // card names, where the dependent op's own status stays "pending" with
  // nothing ever telling the scribe it never left the device.
  function isBlockedOnStuckRide(op, ops) {
    if (!isBlockedByUnsyncedRide(op, ops)) return false;
    const rideOp = ops.find(
      (o) => o.type === "createRide" && o.tempId === op.tempId,
    );
    return Boolean(
      rideOp && (isSurfacedError(rideOp) || isHeldForCurrentOwner(rideOp)),
    );
  }

  async function refreshPending() {
    const ops = await peekOutbox();
    pendingCount = ops.length;
    // #914 — debounced count: an op that's merely failed once (status
    // "error", attempts 1) is still counted in pendingCount (it's honestly
    // pending) but only counts as an "error" to the UI once isSurfacedError
    // says so — see that function for the permanent-vs-debounced rule.
    // #1923 F4 — a held op that also carries status:"error" (attempted
    // under its real owner, before the swap) must not ALSO count as a
    // surfaced error: it will never be retried under this session (the
    // `ready` filter excludes it regardless), so "retrying…" would be false
    // for it — exactly the lie #1921 built isSurfacedError to remove. One
    // op, one state, one wording: held wins.
    errorCount = ops.filter(
      (o) => isSurfacedError(o) && !isHeldForCurrentOwner(o),
    ).length;
    terminalCount = ops.filter((o) => o.status === "terminal").length;
    heldCount = ops.filter(isHeldForCurrentOwner).length;
    // #2031 AC3 — excludes anything already counted in heldCount so a op
    // that happens to be BOTH (its own owner also mismatches) isn't shown
    // twice under two different badges — "one op, one state, one wording",
    // same rule #1923 F4 already applies between errorCount and heldCount.
    blockedCount = ops.filter(
      (o) => !isHeldForCurrentOwner(o) && isBlockedOnStuckRide(o, ops),
    ).length;
    // #1980 — ALL surfaced ops, not just createRide: a manual ride still on
    // its temp id can surface through a score-sheet op (entityKey
    // "<tempId>::<judgeId>", useScoreSheet.js) or a utility-event op
    // (entityKey bare tempId, useRideUtilityEvents.js, which carries no
    // `tempId` field at all so the ordering guard below doesn't hold it
    // back) just as easily as through its own createRide op — either one
    // 4xx-ing against a still-temp rideId means the ride is showing a sync
    // issue, and it must drop out of the separate "N unsynced rides" count
    // exactly like a surfaced createRide op does. Match entityKey the same
    // way manualRides.js's checkDiscardGuard does (bare tempId, or
    // "tempId::suffix") by taking the segment before "::".
    const nextErroredRideKeys = new Set();
    for (const o of ops) {
      if (!isSurfacedError(o) || isHeldForCurrentOwner(o)) continue;
      const rideKey = o.entityKey?.split("::")[0];
      if (rideKey) nextErroredRideKeys.add(rideKey);
    }
    erroredRideKeys = setsEqual(erroredRideKeys, nextErroredRideKeys)
      ? erroredRideKeys
      : nextErroredRideKeys;
    emit();
  }

  // ── Online-state inputs (pushed by ScoreContext) ─────────────────────────

  // #1762 — going offline cancels the one-shot retry TIMER (clearRetryTimer,
  // below) but deliberately leaves each errored op's nextRetryAt alone (see
  // that branch's own comment). flush()'s `ready` filter still skips any op
  // whose nextRetryAt is in the future, so an op that failed once while
  // online, entered its backoff window, and then saw an offline/online blip
  // before that window elapsed used to sit unsynced until the window
  // happened to elapse on its own — up to RETRY_MAX_MS (60s) — even though
  // the network that actually caused the failure is back. Clearing
  // nextRetryAt on exactly this transition (not on every flush tick, which
  // would defeat backoff for an op that kept failing the whole time it
  // stayed online) makes the very next flush() pick it back up immediately.
  // Terminal ops are excluded on purpose: #1921 already decided those never
  // auto-retry, only a human's retryOp does. A held op is excluded for the
  // same reason handleAuthFailure and the `ready` filter itself exclude one
  // — it isn't blocked on a failure reconnecting can fix, it's blocked on
  // ownership.
  async function clearBackoffOnReconnect() {
    const ops = await peekOutbox();
    for (const op of ops) {
      if (op.status !== "error") continue;
      if (isHeldForCurrentOwner(op)) continue;
      if (!op.nextRetryAt) continue;
      await updateOutboxOp(op.opId, { nextRetryAt: 0 });
    }
  }

  function onTransition(wasOnline) {
    const nowOnline = isOnline();
    if (nowOnline === wasOnline) {
      emit();
      return undefined;
    }
    emit();
    if (nowOnline) {
      startLoop();
      // Fire-and-forget in production (ScoreContext/OfflineIndicator never
      // await this); returned so tests can await the full reconnect drain.
      // reconnecting drains queued work + pulls fresh data, after first
      // clearing any backoff earned before the offline blip (#1762).
      return clearBackoffOnReconnect().then(() => sync());
    }
    stopLoop();
    // No point waking up to retry a POST while offline; flush() will
    // re-arm its own backoff timer once back online and it hits an error.
    clearRetryTimer();
    return undefined;
  }

  function setApi(next) {
    apiRef = next;
  }
  // #1289 — same shape as setApi: the singleton is created before React exists,
  // so ScoreContext hands the OIDC-backed renew in on mount.
  function setAuthFailureHandler(next) {
    authHandler = next;
  }
  function setBrowserOnline(value) {
    const was = isOnline();
    browserOnline = value;
    return onTransition(was);
  }
  function setForcedOffline(value) {
    const was = isOnline();
    forcedOffline = value;
    return onTransition(was);
  }

  // ── Flush loop ───────────────────────────────────────────────────────────

  function startLoop() {
    if (intervalId || !isOnline()) return;
    intervalId = timers.set(() => void flush(), flushIntervalMs);
  }
  function stopLoop() {
    if (intervalId) {
      timers.clear(intervalId);
      intervalId = null;
    }
  }

  // #882 — exponential backoff for stranded (status:"error") outbox ops
  // (root cause of the CDS 2026-07-17 orphan-sheet incident: a failed
  // createRide POST sat at status:"error" with nothing forcing a re-drain
  // except the coarse flushIntervalMs poll). Capped so a permanently-broken
  // op (bad payload, dead auth) doesn't hammer the API forever.
  const RETRY_BASE_MS = 3000;
  const RETRY_MAX_MS = 60000;
  const backoffMs = (attempts) =>
    Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** Math.max(0, attempts - 1));

  function clearRetryTimer() {
    if (retryTimerId != null) {
      timers.clear(retryTimerId);
      retryTimerId = null;
    }
  }

  // #914 — the red sync-error indicator must not alarm on a single transient
  // blip that the very next 3s retry quietly clears: a momentary ringside
  // wifi drop looks identical, after just one failed attempt, to a permanent
  // 400. `status:"error"`/`attempts`/backoff (above) are unchanged — those
  // still flip on the FIRST failure and drive retry/hold scheduling. What's
  // new is a separate "is this surfaced to the UI" test, applied only when
  // computing errorCount (below):
  //   - a permanent failure (4xx validation) surfaces immediately — it's a
  //     real, non-recoverable problem that will never clear on its own.
  //   - a transient failure (network error / no response / 5xx) only
  //     surfaces once it's failed, been retried, and failed AGAIN — i.e. it
  //     survived past its first backoff window and is genuinely wedged, not
  //     a one-off blip.
  const SURFACE_AFTER_ATTEMPTS = 2;

  // #1289 — how many silent renews may pass without a single op getting
  // through before we give up and show the scribe the sign-in prompt. >1
  // because the first retry after a renew can legitimately still carry the old
  // token (the FE rebuilds its API client on the React render that follows the
  // renew, which lands a beat after the engine's immediate retry) — that's one
  // wasted round, not a dead session. Three fruitless rounds across the 5 s
  // poll is ~10 s, which is fast enough to be useful and slow enough not to
  // cry wolf at a scribe mid-ride.
  const MAX_SILENT_RENEWS = 3;

  // installServerErrorInterceptor (common-fe) rewrites err.message to a
  // generic string but leaves err.response.status intact — that's what we
  // classify on. No response at all (network error/timeout, axios never got
  // a response) is treated the same as a 5xx: transient, may clear itself.
  function classifyError(err) {
    const status = err?.response?.status;
    // #1289 — a dead/expired session is NOT the same as a bad payload: a 401
    // means the TOKEN is bad, which a renew fixes, and only this case has an
    // action the scribe can take. Classified apart so it gets the renew
    // ladder rather than a generic "sync issue, retrying…" badge that can
    // never clear.
    if (status === 401) return "auth";
    // #1562 — a 403 is NOT the same failure as a 401, even though both are
    // "identity" 4xxs. A 401 means the credential itself is dead; a 403 means
    // the token verified FINE and score-api is refusing this principal on
    // purpose (permission, wrong org, a revoked entitlement). No amount of
    // renewing, signing out, or signing back in changes who the principal is
    // allowed to be, so a 403 must never reach the renew ladder or set
    // authExpired: doing so latched the "Session expired — tap to sign in"
    // chip forever on a permission refusal that renewing can never resolve
    // (repro'd end to end on stage 2026-08-04). It falls through to the
    // generic 4xx bucket below instead — same treatment as a bad payload:
    // unrecoverable by blind retry, but surfaced immediately as a real,
    // visible sync-issue rather than lying with a "sign in" prompt.
    if (typeof status === "number" && status >= 400 && status < 500) {
      return "permanent";
    }
    return "transient";
  }

  // Whether an errored op should count toward the UI-facing errorSync total.
  // #1289 — "auth" is excluded on purpose: it has its own dedicated UI state
  // (authExpired), and showing both a red sync-issue chip AND a session-expired
  // prompt for one underlying cause reads as two problems instead of one.
  // #1562 — a 403 is classified "permanent" (above), not "auth", precisely so
  // it lands in the `permanent` branch below: the scribe gets a real,
  // immediate signal (the red sync-issue chip, errorSync > 0) instead of
  // errorSync staying 0 with an unclearable session-expired chip as the only
  // symptom.
  // #1921 — a "terminal" op (below) is a permanent failure that has already
  // given up retrying; it must keep surfacing here exactly like "error" did,
  // so the red chip and the errorSync count don't just go quiet the moment
  // an op stops being retried.
  function isSurfacedError(op) {
    if (op.status !== "error" && op.status !== "terminal") return false;
    if (op.errorClass === "auth") return false;
    if (op.errorClass === "permanent") return true;
    return (op.attempts ?? 0) >= SURFACE_AFTER_ATTEMPTS;
  }

  // #1289 — the renew ladder. Try to recover the session silently; if that
  // works, clear the auth-failed ops' error state so they retry immediately
  // (rather than sitting out a backoff window earned by a problem that no
  // longer exists) and the scribe never sees anything. If it fails, surface
  // the one escape hatch a kiosk needs.
  //
  // Fails CLOSED: ops are never dropped on either path — a failed renew leaves
  // the work queued exactly where it was, so re-logging in later still drains
  // it. Guarded by renewInFlight so a burst of failing ops triggers one renew,
  // not one per op.
  async function handleAuthFailure() {
    if (renewInFlight) return;
    // Out of silent attempts: the renew keeps "working" and the API keeps
    // saying no, so no amount of further renewing fixes it. Escalate.
    if (renewsSinceProgress >= MAX_SILENT_RENEWS) {
      authExpired = true;
      emit();
      return;
    }
    renewInFlight = true;
    try {
      renewsSinceProgress += 1;
      const recovered = authHandler ? await authHandler() : false;
      if (recovered) {
        authExpired = false;
        const ops = await peekOutbox();
        for (const op of ops) {
          if (op.errorClass !== "auth") continue;
          // #1923 F5 — a held op (owned by a different sub, or unknown)
          // must not be reset here: the #1921 detail view's attempts/
          // errorClass history for it is exactly what a discard decision is
          // made from, and this is a renew succeeding for THIS session's
          // credential, not for whoever actually owns the held op.
          if (isHeldForCurrentOwner(op)) continue;
          await updateOutboxOp(op.opId, {
            status: "pending",
            attempts: 0,
            errorClass: null,
            nextRetryAt: 0,
          });
        }
        await refreshPending();
        await flush();
        return;
      }
      authExpired = true;
    } catch {
      // A renew that throws is a renew that failed — same outcome, and never
      // a reason to lose the queued work.
      authExpired = true;
    } finally {
      renewInFlight = false;
      emit();
    }
  }

  // Arms a dedicated one-shot wake-up at the earliest op's backoff deadline,
  // independent of the steady flushIntervalMs poll and of online/offline
  // transitions — so an errored op gets retried on its own schedule even if
  // the scribe never reconnects/toggles anything.
  function scheduleBackoffRetry(delayMs) {
    clearRetryTimer();
    retryTimerId = timers.set(() => {
      clearRetryTimer();
      // Returned (not void-discarded) so callers driving a fake timer in
      // tests can `await` the resulting drain; a real setInterval callback
      // ignores the return value either way.
      return flush();
    }, Math.max(0, delayMs));
  }

  // Drain the outbox in FIFO (seq) order. Each op is removed on success; on
  // failure it's marked errored (attempts bumped, nextRetryAt pushed out by
  // backoffMs) and left in place to retry. We re-peek each step (not a stale
  // snapshot) because applying a createRide can rewrite queued sheet ops via
  // remapTempRide — the re-keyed op must be picked up fresh. `processed`
  // prevents reprocessing an op within a single pass; an errored op whose
  // backoff window hasn't elapsed is left for the NEXT pass (either the
  // regular poll or the dedicated retry timer armed below).
  //
  // #882 — CRITICAL invariant: an op carrying a `tempId` (a score-sheet write
  // queued before its manual ride synced) must NEVER drain while that ride's
  // own createRide op is still sitting in the outbox — pending OR errored.
  // Without this, a failed createRide op used to just get skipped-and-marked
  // (via `processed`) and the loop happily moved on to the NEXT ready op,
  // which was the score sheet — POSTing it with the client's temp
  // `manual-<classId>-...` rideId, a value the server has never seen. That's
  // exactly the CDS 2026-07-17 CNO 462 orphan sheet: the sheet flushed, the
  // ride row didn't, and remapTempRide never got a chance to rewrite the
  // sheet op's rideId because it had already been sent. Holding dependent ops
  // here means the sheet can't flush ahead of its ride under ANY failure mode
  // — it waits for the ride to actually exist server-side first.
  async function flush() {
    // #1923 F2 — nothing that needs a bearer token flushes while nobody is
    // signed in: bail out before the drain loop even peeks the outbox.
    if (!isOnline() || !apiRef || draining || !getCurrentOwnerSub()) return;
    draining = true;
    const processed = new Set();
    let earliestBlockedAt = null;
    // #1289 — set when any op in this pass failed on auth; acted on after the
    // drain loop releases `draining`, since the renew path re-enters flush().
    let authFailedThisPass = false;
    try {
      while (isOnline()) {
        const ops = await peekOutbox();
        const now = Date.now();
        const ready = ops.find((o) => {
          if (processed.has(o.opId)) return false;
          // #1921 — a terminal op has already given up: this filter alone
          // never lets flush() pick it again. This is the actual fix —
          // before this, a permanent 4xx op still sat in "error" and kept
          // getting re-tried here on the normal backoff schedule forever.
          // (A terminal op CAN still leave "terminal": a human's discardOp
          // below, or db.js's enqueue() upserting the same opId back to
          // "pending" when a fresh edit to the same sheet queues again —
          // that revival is intentional, new data deserves a new attempt.)
          if (o.status === "terminal") return false;
          // #1923 — an op owned by someone other than whoever is signed in
          // now (or by no one known) never reaches the network under this
          // session's token. It stays exactly where it is — pending, not
          // errored, not terminal — so it resumes normally the moment its
          // real owner signs back in and currentOwnerSub matches again.
          if (isHeldOp(o, getCurrentOwnerSub())) return false;
          if (o.status === "error" && o.nextRetryAt && o.nextRetryAt > now) {
            return false;
          }
          // #2031 — this same check is now shared with the display-side
          // "blocked" surfacing below (isBlockedOnStuckRide), via db.js's
          // isBlockedByUnsyncedRide, so the engine and the UI can never read
          // this ordering gate differently.
          if (isBlockedByUnsyncedRide(o, ops)) return false;
          return true;
        });
        if (!ready) {
          // Anything still in the outbox at this point is either blocked on
          // its ride's createRide op or IS a createRide op sitting in its
          // backoff window — either way, the wake-up we want is the earliest
          // errored op's nextRetryAt (not gated on `processed`: the blocking
          // createRide op was itself attempted-and-errored earlier in THIS
          // pass, so it's already in `processed`, but its nextRetryAt is
          // still the right thing to schedule against).
          // #1923 F1 — a held op that is ALSO status:"error" (the transient
          // class: it was attempted, under its real owner, before the swap)
          // is never `ready` above regardless of its nextRetryAt, so it must
          // not seed earliestBlockedAt either. Its nextRetryAt is frozen in
          // the past forever (nothing ever attempts it again to push it
          // forward) — left in, `Math.min` picks it, `scheduleBackoffRetry`'s
          // Math.max(0, delayMs) clamps the negative delay to 0, and flush()
          // re-fires immediately, forever: a busy loop hitting IndexedDB on
          // every tick with nothing to show for it.
          const errored = ops.filter(
            (o) => o.status === "error" && !isHeldForCurrentOwner(o),
          );
          if (errored.length) {
            earliestBlockedAt = Math.min(
              ...errored.map((o) => o.nextRetryAt ?? now),
            );
          }
          break;
        }
        processed.add(ready.opId);
        try {
          await applyOp(ready);
          await removeOutboxOp(ready.opId);
          // #1289 — real progress: whatever the session trouble was, it's over.
          renewsSinceProgress = 0;
          authExpired = false;
        } catch (err) {
          const attempts = (ready.attempts ?? 0) + 1;
          // #914 — classified once per failure so isSurfacedError can tell
          // a real 4xx apart from a network/5xx blip that might clear
          // itself on the very next retry.
          const errorClass = classifyError(err);
          // #1562 — the interceptor rewrites err.message to a generic string
          // but leaves err.response.status intact; capture it separately from
          // lastError so the UI can explain a known status (e.g. 403) in
          // words without having to parse the message text.
          const httpStatus = err?.response?.status ?? null;
          // #2111 — installServerErrorInterceptor (common-fe) already stashes
          // the real backend detail on err.serverErrorDetail before
          // overwriting err.message with the generic "Server Error" string
          // (see the interceptor's own comment, and #1562's above) — that
          // stashed detail is exactly the diagnostic-grade text the AC5 raw
          // "error detail" expander needs (a 409's actual conflict reason,
          // not just its status code), so it's captured here rather than
          // re-deriving it from err.message, which by this point has already
          // been overwritten to the generic string.
          const rawErrorDetail = err?.serverErrorDetail ?? null;
          if (errorClass === "permanent") {
            // #1921 — a permanent (non-401 4xx) failure is a decision, not a
            // wait: this op reaches a genuinely TERMINAL state right here on
            // its first failure. It is never scheduled by
            // scheduleBackoffRetry and never picked up by the `ready` filter
            // above again — no more retries, no more API calls. It stays in
            // the outbox (fail-closed: nothing here ever removes it) until a
            // human explicitly discards it via discardOp.
            await updateOutboxOp(ready.opId, {
              status: "terminal",
              attempts,
              errorClass,
              httpStatus,
              lastError: String(err?.message ?? err),
              rawErrorDetail,
              nextRetryAt: null,
            });
          } else {
            await updateOutboxOp(ready.opId, {
              status: "error",
              attempts,
              errorClass,
              httpStatus,
              lastError: String(err?.message ?? err),
              rawErrorDetail,
              nextRetryAt: Date.now() + backoffMs(attempts),
            });
            // #1289 — a dead session won't fix itself by retrying the same
            // credential. Kick the renew ladder. Not awaited inside the
            // drain loop: handleAuthFailure re-enters flush() on success,
            // and awaiting it here would deadlock on `draining`.
            if (errorClass === "auth") authFailedThisPass = true;
          }
        }
      }
    } finally {
      draining = false;
      await refreshPending();
      if (earliestBlockedAt != null && isOnline()) {
        scheduleBackoffRetry(earliestBlockedAt - Date.now());
      } else {
        clearRetryTimer();
      }
      // #1289 — after `draining` is released, so handleAuthFailure's
      // re-entrant flush() on a successful renew isn't swallowed by the guard.
      if (authFailedThisPass) await handleAuthFailure();
    }
  }

  async function applyOp(op) {
    switch (op.type) {
      case "createRide": {
        const created = await apiRef.createRide(op.payload);
        if (op.tempId && created?.id) {
          await remapTempRide(op.tempId, created.id, created);
        }
        return;
      }
      case "createScoreSheet": {
        let created;
        try {
          created = await apiRef.createScoreSheet(op.payload.body);
        } catch (err) {
          // #2409 — score-api's createScoreSheet guards (orgId, rideId,
          // judge position) uniqueness with an atomic conditional write and
          // refuses a second create for the same seat with 409
          // duplicate_score_sheet (data.existingSheetId names the sheet that
          // won). That is exactly the shape a flaky-connection replay of
          // this very op produces: the first attempt actually landed
          // server-side, the ack never reached this device, and now this
          // queued retry collides with its own earlier success. Treat it as
          // success — adopt the existing sheet rather than throwing into
          // classifyError's "permanent" bucket, which would otherwise wedge
          // this op in the outbox forever retrying a create that can only
          // ever 409 again (installServerErrorInterceptor rewrites
          // err.message but leaves err.response.status/data intact, #1562).
          if (
            err?.response?.status === 409 &&
            err?.response?.data?.code === "duplicate_score_sheet"
          ) {
            const existingSheetId = err.response.data?.data?.existingSheetId;
            try {
              const adopted = await apiRef.getScoreSheet(existingSheetId);
              if (adopted?.id) await scoreSheets.put(adopted);
            } catch {
              // Couldn't fetch the adopted sheet right now (offline blip on
              // the very next call, etc). Cache a minimal stub keyed to the
              // real server id anyway, so the ride's cached sheet stops
              // looking blank and nothing re-queues a create that will only
              // ever 409 again; a later hydration pass fills in the rest.
              await scoreSheets.put({
                ...op.payload.body,
                id: existingSheetId,
              });
            }
            return;
          }
          throw err;
        }
        if (created?.id) await scoreSheets.put(created);
        return;
      }
      case "updateScoreSheet": {
        const updated = await apiRef.updateScoreSheet(
          op.payload.serverId,
          op.payload.body,
        );
        if (updated?.id) await scoreSheets.put(updated);
        return;
      }
      case "completeScoreSheet": {
        // serverId may be unknown when queued offline; resolve it from the
        // sheet the create op cached (drains earlier — lower seq).
        let serverId = op.payload.serverId;
        if (!serverId && op.payload.rideId && op.payload.judgeId) {
          const cached = await scoreSheets.byRideJudge(
            op.payload.rideId,
            op.payload.judgeId,
          );
          serverId = cached[0]?.id ?? null;
        }
        if (!serverId) throw new Error("completeScoreSheet: no server id yet");
        const done = await apiRef.completeScoreSheet(serverId);
        if (done?.id) await scoreSheets.put(done);
        return;
      }
      case "confirmScoreSheetMarks": {
        // #698 — same server-id resolution as completeScoreSheet: the
        // create op may not have drained yet when this was queued offline.
        let serverId = op.payload.serverId;
        if (!serverId && op.payload.rideId && op.payload.judgeId) {
          const cached = await scoreSheets.byRideJudge(
            op.payload.rideId,
            op.payload.judgeId,
          );
          serverId = cached[0]?.id ?? null;
        }
        if (!serverId) throw new Error("confirmScoreSheetMarks: no server id yet");
        const confirmed = await apiRef.confirmScoreSheetMarks(serverId);
        if (confirmed?.id) await scoreSheets.put(confirmed);
        return;
      }
      // #1751 — one panel judge signs one row's sheet. The class-level
      // signing ceremony fans this out over every row in the grid, so a
      // 25-rider DSM class queues 25 of these per judge; they drain through
      // the same outbox as every other mutation, which is what makes the
      // ceremony survive the network dying halfway through it.
      case "panelSignScoreSheet": {
        // Same server-id resolution as completeScoreSheet/confirmScoreSheetMarks:
        // the create op may not have drained yet when this was queued offline.
        let serverId = op.payload.serverId;
        if (!serverId && op.payload.rideId && op.payload.judgeId) {
          const cached = await scoreSheets.byRideJudge(
            op.payload.rideId,
            op.payload.judgeId,
          );
          serverId = cached[0]?.id ?? null;
        }
        if (!serverId) throw new Error("panelSignScoreSheet: no server id yet");
        const signed = await apiRef.panelSignScoreSheet(
          serverId,
          op.payload.panelJudgeId,
        );
        if (signed?.id) await scoreSheets.put(signed);
        return;
      }
      // card #139 — raise/clear a BLOOD/LAMENESS utility event. No local
      // cache to reconcile here: useRideUtilityEvents' own poll picks up the
      // server-confirmed state on its next tick (or immediately, since the
      // caller also awaits an explicit refresh right after flush()).
      case "raiseUtilityEvent": {
        await apiRef.raiseUtilityEvent(op.payload.rideId, op.payload.type);
        return;
      }
      case "clearUtilityEvent": {
        await apiRef.clearUtilityEvent(op.payload.rideId, op.payload.eventId);
        return;
      }
      default:
        throw new Error(`unknown outbox op type: ${op.type}`);
    }
  }

  // A synced manual ride gets a real server id: swap it into the cached ride,
  // and rewrite any queued score-sheet ops + cached sheets that referenced the
  // temp id so they drain against the real ride (#386).
  async function remapTempRide(tempId, serverId, serverRide) {
    if (serverRide) await rides.put(serverRide);
    if (tempId !== serverId) await rides.remove(tempId);

    const ops = await peekOutbox();
    for (const op of ops) {
      if (op.tempId !== tempId || op.type === "createRide") continue;
      // Swap the temp id inside the entityKey + opId, preserving the op's own
      // prefix (scoreSheet: / complete:) and seq (FIFO position).
      const entityKey = op.entityKey.replace(`${tempId}::`, `${serverId}::`);
      const opId = op.opId.replace(`${tempId}::`, `${serverId}::`);
      await removeOutboxOp(op.opId);
      await put("outbox", {
        ...op,
        opId,
        entityKey,
        tempId: null,
        payload: {
          ...op.payload,
          rideId: serverId,
          ...(op.payload.body
            ? { body: { ...op.payload.body, rideId: serverId } }
            : {}),
        },
      });
    }

    const sheets = await getAll("scoreSheets");
    for (const sheet of sheets) {
      if (sheet.rideId !== tempId) continue;
      await scoreSheets.put({ ...sheet, rideId: serverId });
    }

    for (const fn of rideSyncedListeners) {
      try {
        fn(tempId, serverId);
      } catch {
        // a listener error must not abort the drain
      }
    }
  }

  // ── Hydration (pull) ─────────────────────────────────────────────────────

  // Walk shows → classes → rides, plus the global people/horses/tests, into
  // the cache. Bounded to the show catalog (pilot scale); per-view revalidation
  // (#421) keeps individual screens fresh as the scribe navigates.
  async function hydrate() {
    if (!isOnline() || !apiRef) return;

    const showList = (await apiRef.listShows()) ?? [];
    await shows.putAll(showList);

    const [peopleList, horseList] = await Promise.all([
      apiRef.listPeople().catch(() => []),
      apiRef.listHorses().catch(() => []),
    ]);
    await people.putAll(peopleList ?? []);
    await horses.putAll(horseList ?? []);

    const testIds = new Set();
    for (const show of showList) {
      const showId = show.id ?? show.showId;
      const classList = (await apiRef.listClasses(showId).catch(() => [])) ?? [];
      await classes.putAll(classList);
      for (const klass of classList) {
        const rideList =
          (await apiRef.listRides(klass.id).catch(() => [])) ?? [];
        await rides.putAll(rideList);
        // #435 — test is a per-RIDE property; prefetch every ride's own test so
        // a mixed-test class scores offline. (class.testId is only a hint.)
        for (const ride of rideList) if (ride.testId) testIds.add(ride.testId);
      }
    }

    const testList = await Promise.all(
      [...testIds].map((id) => apiRef.getTest(id).catch(() => null)),
    );
    await tests.putAll(testList.filter(Boolean));

    await setMeta("lastPulledAt", new Date().toISOString());
  }

  async function sync() {
    await flush();
    await hydrate();
  }

  // #1921 — every op currently surfaced to the UI (terminal, or a transient
  // failure that's proven itself stuck) — what the sync-status detail view
  // lists. Read-only; does not touch the outbox.
  async function listSurfacedOps() {
    const ops = await peekOutbox();
    // #1923 — a held op is just as much "something a scribe needs to see and
    // can discard" as an error/terminal one, so it belongs in the same
    // detail-view list (1921's stuck-op view), not a separate surface.
    // #2031 AC3 — likewise for a blocked op (see isBlockedOnStuckRide): it
    // is marked with a non-persisted `blockedOnStuckRide` flag (computed
    // fresh on every call, never written to IndexedDB) so the display layer
    // can tell this cause apart from an ownership hold without re-deriving
    // the #882 tempId match itself.
    return ops
      .map((o) => ({ ...o, blockedOnStuckRide: isBlockedOnStuckRide(o, ops) }))
      .filter(
        (o) =>
          isSurfacedError(o) || isHeldForCurrentOwner(o) || o.blockedOnStuckRide,
      );
  }

  // #1921 — the FULL current outbox (pending + error + terminal), completely
  // unfiltered, for the "copy outbox" export (AC7). listSurfacedOps exists
  // to drive the detail LIST, which is deliberately scoped to stuck ops
  // (AC4) -- but the export exists to answer a different question ("does
  // this op's queued payload hold a later edit than the stored sheet"),
  // asked about ANY queued op including one still pending or one that
  // hasn't crossed SURFACE_AFTER_ATTEMPTS yet, so it must never drop rows
  // the way isSurfacedError does. Read-only; does not touch the outbox.
  async function listAllOps() {
    return peekOutbox();
  }

  // #1921 — explicit, human-initiated discard of exactly one outbox op.
  // There is no other code path anywhere in this engine that removes an op
  // without it having actually applied (fail-closed is the whole point of
  // the outbox); this is the single, deliberate exception, and it only ever
  // runs when a person taps Discard on a named op in the detail view and
  // confirms.
  async function discardOp(opId) {
    await removeOutboxOp(opId);
    await refreshPending();
  }

  // #2111 — human-initiated retry of exactly one surfaced (error/terminal)
  // op, from the Stuck sync items dialog's per-op Retry button. Nudges JUST
  // that op back into flush()'s "ready" pool: a terminal op is demoted to
  // "error" (the only state the ready filter above will ever pick up again
  // — see flush()'s `if (o.status === "terminal") return false;`) and its
  // backoff window is cleared (nextRetryAt: 0) so it isn't blocked behind a
  // stale wait. attempts/errorClass are deliberately left as they were: a
  // retry that fails again must not un-surface itself just because a human
  // asked for one more try (isSurfacedError's transient branch requires
  // attempts >= SURFACE_AFTER_ATTEMPTS, and resetting attempts to 0 here
  // would drop a still-failing op out of listSurfacedOps for one tick,
  // reading as "fixed" when it plainly is not). A held op is left untouched:
  // it is not blocked on a failure retrying can fix, it is blocked on
  // ownership. This is checked explicitly below (not just left to flush()'s
  // own isHeldOp gate) because a held-but-TERMINAL op is the case that used
  // to slip through: isHeldForCurrentOwner excludes status:"terminal" by
  // design (see its own comment above), so demoting terminal->error here
  // used to make a "stuck, needs a human" row silently and permanently
  // reclassify as merely "held" (heldCount/isHeldForCurrentOwner) once the
  // demotion cleared its terminal status. Guarding on isHeldOp directly,
  // before any status write, means a held+terminal op is never touched.
  async function retryOp(opId) {
    const op = await getOutboxOp(opId);
    if (!op) return;
    if (isHeldOp(op, getCurrentOwnerSub())) return;
    if (op.status === "error" || op.status === "terminal") {
      await updateOutboxOp(opId, { status: "error", nextRetryAt: 0 });
    }
    await refreshPending();
    await flush();
  }

  // #2031 AC1/AC2 — lets a scribe re-adopt a legacy (pre-#1923) outbox op
  // that has NO owner at all, the one exit #1923 left missing: hold-and-
  // surface was correct, discard-only wasn't. Guarded on isOwnerless, NOT on
  // isHeldOp/isHeldForCurrentOwner — those also read true for an op owned by
  // a DIFFERENT KNOWN sub, and AC2 is a hard boundary: claiming must never
  // be offered, let alone succeed, for a foreign-but-known owner (that would
  // let one scribe silently steal another still-signed-in scribe's queued
  // writes, the WEC 2026-08-23 hazard #1923 exists to prevent). Requires a
  // signed-in sub, mirroring flush()'s own bail-out: claiming into "no one"
  // is meaningless and would just create a second ownerless op. One action,
  // no confirm dialog (unlike discardOp) — claiming is non-destructive, the
  // op can still be discarded afterward if it turns out wrong.
  async function claimOp(opId) {
    const sub = getCurrentOwnerSub();
    if (!sub) return;
    const op = await getOutboxOp(opId);
    if (!op || !isOwnerless(op)) return;
    await updateOutboxOp(opId, { owner: sub });
    await refreshPending();
    await flush();
  }

  function start() {
    void refreshPending();
    if (isOnline()) startLoop();
  }
  function stop() {
    stopLoop();
    clearRetryTimer();
  }

  return {
    isOnline,
    subscribe,
    onRideSynced,
    setApi,
    setAuthFailureHandler,
    setBrowserOnline,
    setForcedOffline,
    // #1923 — re-exported from db.js: it's the single source of truth for
    // "who owns this op", used by both enqueue() (stamping) and this engine
    // (gating flush + counting held ops), so ScoreContext talks to the
    // engine the same way it already does for setApi/setBrowserOnline
    // rather than importing db.js directly.
    setCurrentOwnerSub,
    flush,
    hydrate,
    sync,
    refreshPending,
    listSurfacedOps,
    listAllOps,
    discardOp,
    retryOp,
    claimOp,
    start,
    stop,
  };
}

// App-wide singleton used by ScoreContext. Tests build their own via
// createSyncEngine() with injected api + timers.
export const syncEngine = createSyncEngine();
